import authService from './authService';
import { ensureOk, readData, readList, toQuery } from './apiResponse';
import type { Category, CategoryFilter, CategoryInput, Movement } from '../types/finance';

/** Nó da árvore de categorias (2 níveis) montada no front a partir da lista plana. */
export interface CategoryNode {
  category: Category;
  children: Category[];
}

const byOrder = (a: Category, b: Category) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name, 'pt-BR');

/** Lista plana (`parent_category_id`) → raízes com filhas, ordenadas por `sort_order` e nome. */
export const buildCategoryTree = (flat: Category[], movement?: Movement | ''): CategoryNode[] => {
  const list = movement ? flat.filter((c) => c.movement === movement) : flat;
  const ids = new Set(list.map((c) => c.id));
  const roots = list.filter((c) => !c.parent_category_id || !ids.has(c.parent_category_id)).sort(byOrder);
  return roots.map((root) => ({
    category: root,
    children: list.filter((c) => c.parent_category_id === root.id).sort(byOrder),
  }));
};

/** Árvore → opções de select em profundidade (raiz, depois "↳ filha"). */
export const flattenCategoryTree = (tree: CategoryNode[]): { category: Category; depth: number }[] =>
  tree.flatMap((n) => [{ category: n.category, depth: 0 }, ...n.children.map((c) => ({ category: c, depth: 1 }))]);

/** Rótulo de opção com recuo: "Moradia" / "↳ Aluguel". */
export const categoryOptionLabel = (category: Category, depth: number): string => `${depth > 0 ? '↳ ' : ''}${category.name}`;

/** Finanças → Categorias (despesa/receita/transferência, 2 níveis). */
class CategoryService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** GET /categories?movement=&include_disabled=1 — lista plana com `transaction_count`. */
  async getAll(filter: CategoryFilter = {}): Promise<Category[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/categories${toQuery({ movement: filter.movement || undefined, include_disabled: filter.include_disabled ? 1 : undefined })}`,
    );
    return readList<Category>(response, 'Falha ao listar as categorias');
  }

  /** POST /categories (409 nome repetido no mesmo pai; 400 mais de 1 nível ou movimento ≠ do pai) */
  async create(input: CategoryInput): Promise<Category> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/categories', input);
    return readData<Category>(response, 'Falha ao criar a categoria');
  }

  /** PUT /categories/:id (sistema: só nome/cor/ícone/ordem; mudar movimento com lançamentos → 409) */
  async update(id: number, input: CategoryInput): Promise<Category> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/categories/${id}`, input);
    return readData<Category>(response, 'Falha ao atualizar a categoria');
  }

  /** DELETE /categories/:id — 409 com subcategorias, lançamentos, regras ou de sistema. */
  async remove(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/categories/${id}`);
    await ensureOk(response, 'Falha ao excluir a categoria');
  }
}

export const categoryService = new CategoryService();
export default categoryService;
